"use client";

import Link from "next/link";
import WorkerEnabledToggle from "@/components/WorkerEnabledToggle";
import WorkerDeleteSection from "@/components/WorkerDeleteSection";
import { useId, useRef, useState } from "react";
import type { FolderNode, SidebarWorker } from "./types";
import { buildFolderTree, workerAttentionCounts } from "./folderTreeModel";
import type { WorkerMovement } from "./useWorkerMoving";
import { AttentionIndicator, attentionSummary } from "./AttentionIndicator";
import { NEEDS_YOU_LABEL } from "@/lib/attention/needsYouLabels";

function folderDestinations(folders: FolderNode[], parentPath = ""): { id: string; label: string }[] {
  return folders.flatMap(folder => {
    const label = parentPath ? `${parentPath} / ${folder.name}` : folder.name;
    return [{ id: folder.id, label }, ...folderDestinations(folder.children, label)];
  });
}

const progressLabel = "Progress not measured — no measure or target defined";

/** Worker navigation with health in Automation and outcome context in Monitoring. */
export function WorkerRow({
  worker: w,
  depth,
  pathname,
  onCloseDrawer,
  movement,
  hrefOverride,
  view = "automation",
}: {
  worker: SidebarWorker;
  depth: number;
  pathname: string;
  onCloseDrawer: () => void;
  movement?: WorkerMovement;
  hrefOverride?: string;
  view?: "automation" | "monitoring";
}) {
  const [organizing, setOrganizing] = useState(false);
  const [openedOnce, setOpenedOnce] = useState(false);
  const organizeButton = useRef<HTMLButtonElement>(null);
  const organizePanelId = useId();
  const workerHref = `/workers/${w.id}`;
  const href = hrefOverride ?? workerHref;
  const active = pathname === workerHref || pathname.startsWith(`${workerHref}/`);
  const counts = workerAttentionCounts(w);
  const healthy = Boolean(w.attentionCounts) && !w.needsYou?.length;
  const activity = [
    w.enabled ? "Enabled" : "Paused",
    attentionSummary(counts),
    w.messageCount > 0 ? `${w.messageCount} message${w.messageCount === 1 ? "" : "s"}` : null,
    w.recentStatusCount > 0 ? `${w.recentStatusCount} status update${w.recentStatusCount === 1 ? "" : "s"} in the last 24h` : null,
    ...(w.needsYou ?? []).map(reason => NEEDS_YOU_LABEL[reason]),
  ].filter(Boolean).join(". ");
  return (
    <div onKeyDown={event => {
      if (event.key === "Escape" && organizing) {
        event.preventDefault();
        event.stopPropagation();
        setOrganizing(false);
        organizeButton.current?.focus();
      }
    }}>
      <div className="group/worker flex flex-wrap items-center">
        <Link
          {...movement?.workerProps(w)}
          href={href}
          aria-label={view === "monitoring" ? w.name : undefined}
          aria-current={active ? "page" : undefined}
          title={view === "monitoring" ? progressLabel : activity || undefined}
          onClick={onCloseDrawer}
          style={{ paddingLeft: `${28 + depth * 14}px` }}
          className={`min-w-0 flex-1 flex h-9 items-center gap-1.5 rounded-lg pr-2.5 text-sm transition ${
            active
              ? "bg-violet-500/10 font-medium text-violet-300"
              : "text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200"
          }`}
        >
          <span data-worker-status-slot className="flex h-3.5 w-8 shrink-0 items-center">
            {view === "monitoring" ? (
              <span role="group" data-monitoring-performance="unmeasured" title={progressLabel} aria-label={progressLabel}
                className="flex h-3.5 w-8 shrink-0 items-end gap-0.5 pb-px">
                {["h-1", "h-2", "h-3"].map(height => <span key={height} data-progress-segment aria-hidden="true"
                  className={`${height} w-[7px] shrink-0 bg-zinc-700`} />)}
              </span>
            ) : <AttentionIndicator counts={counts} variant="hearts" healthy={healthy} />}
          </span>
          <span data-worker-name className="min-w-0 flex-1 truncate">{w.name}</span>
        </Link>
        {view === "automation" && <WorkerEnabledToggle workerId={w.id} workerName={w.name} enabled={w.enabled} variant="icon" />}
        {movement && <button
          ref={organizeButton}
          type="button"
          aria-label={`Organize ${w.name}`}
          aria-expanded={organizing}
          aria-controls={organizing ? organizePanelId : undefined}
          onClick={() => { setOpenedOnce(true); setOrganizing(open => !open); }}
          className={`shrink-0 cursor-pointer rounded px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100 ${organizing ? "opacity-100" : "opacity-60 sm:opacity-0 sm:group-hover/worker:opacity-100 sm:focus-visible:opacity-100"}`}
        >⋯</button>}
      </div>
      {movement && openedOnce && <div id={organizePanelId} hidden={!organizing} className="mb-1 ml-3 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
        <label className="block px-2 py-1 text-xs text-zinc-500">
          Move to folder
          <select
            aria-label={`Move ${w.name} to folder`}
            value={movement.folders.some(f => f.id === w.folderId) ? w.folderId! : ""}
            disabled={Boolean(movement.moving)}
            onChange={event => void movement.move(w.id, event.target.value || null)}
            className="mt-1 w-full cursor-pointer rounded border border-zinc-700 bg-zinc-900 p-1.5 text-xs text-zinc-200 disabled:cursor-wait disabled:opacity-50"
          >
            <option value="">Top level</option>
            {folderDestinations(buildFolderTree(movement.folders)).map(folder => <option key={folder.id} value={folder.id}>{folder.label}</option>)}
          </select>
        </label>
        <WorkerDeleteSection workerId={w.id} workerName={w.name} variant="menu" />
      </div>}
    </div>
  );
}
